import { useTranslation } from 'react-i18next';
import { useRef, useEffect } from 'react';
import { columnDisplay } from '../../../../shared/format-date.js';
import { useAppCtx } from '../../app-context.jsx';
import { Link } from '../navigation/link.jsx';
import { ActionMenu } from '../interactive/action-menu.jsx';
import { ClickableRow } from '../interactive/clickable-row.jsx';
import { Amount } from '../display/amount.jsx';
import { Button } from '../interactive/button.jsx';
import { Pill } from '../display/pill.jsx';
import { TagPills } from '../display/tag-pills.jsx';

/**
 * @param {Object} props
 * @param {Array<{ key: string, label: string, align?: string, getValue: (c: any) => any, getDisplayValue?: (c: any) => string, getSortValue?: (c: any) => any, priority?: number, type?: string, getCurrency?: (c: any) => any, getPillVariant?: (c: any) => 'default'|'success'|'muted', linkTo?: (row: any) => string|undefined, getTags?: (row: any) => string[] }>} props.columns
 * @param {string} [props.sortKey]
 * @param {number} props.sortDir
 * @param {(key: string) => void} props.handleSort
 * @param {any[]} props.sortedRows
 * @param {number} props.maxVisiblePriority
 * @param {boolean} props.loading
 * @param {string} [props.error]
 * @param {Array<{ fieldKey: string, value: string }>} props.filterClauses
 * @param {(clauses: Array<{ fieldKey: string, value: string }>) => void} props.handleFilterChange
 * @param {string} props.emptyMessage
 * @param {(row: any, columns: Array<any>, maxPriority: number, actions: Array<any>, nav?: { rowLink?: (row: any) => string|undefined, navigate?: (path: string) => void }) => import('react').ReactElement | null} [props.renderRow]
 * @param {(row: any) => Array<{ label: string, onClick: () => void, to?: string, danger?: boolean }>} props.getActionItems
 * @param {(path: string|undefined) => string|undefined} props.appendReturnTo
 * @param {(row: any) => string|undefined} [props.rowLink]
 * @param {(path: string) => void} props.navigate
 * @param {string} [props.effectiveReturnTo]
 * @param {(row: any) => void} [props.onRowClick]
 * @param {number} [props.visibleCount]
 * @param {() => void} [props.onLoadMore]
 * @param {number} [props.renderBatch]
 * @param {boolean} [props.hasMorePages] — server-side keyset pagination: more rows available on the server
 * @param {() => void} [props.onFetchMore] — fetch and append the next server page
 * @param {boolean} [props.loadingMore] — a next-page fetch is in flight (spinner on the Load more row)
 * @returns {JSX.Element}
 */
export function TableBody({ columns, sortedRows, sortKey, sortDir, handleSort, maxVisiblePriority, loading, error, filterClauses, handleFilterChange, emptyMessage, renderRow, getActionItems, appendReturnTo, rowLink, navigate, effectiveReturnTo, onRowClick, visibleCount, onLoadMore, renderBatch, hasMorePages, onFetchMore, loadingMore }) {
	const { t } = useTranslation();
	const { dateFormat } = useAppCtx();

	const sentinelRef = useRef(null);
	const hasMore = renderBatch && visibleCount < sortedRows.length;

	useEffect(() => {
		let cleanup;
		if (hasMore && onLoadMore) {
			const el = sentinelRef.current;
			if (el) {
				const observer = new IntersectionObserver(
					([entry]) => {
						if (entry.isIntersecting) { onLoadMore(); }
					},
					{ rootMargin: '200px' },
				);
				observer.observe(el);
				cleanup = () => observer.disconnect();
			}
		}
		return cleanup;
	}, [hasMore, onLoadMore]);

	const displayRows = renderBatch ? sortedRows.slice(0, visibleCount) : sortedRows;

	return (
		<div className="table-wrap">
			<table>
				<thead>
					<tr>
						{columns.map((col) => {
							const colPriority = col.priority ?? 1;
							if (colPriority > maxVisiblePriority) {return null;}
							const cls = [sortKey === col.key ? 'sorted' : '', col.align === 'right' ? 'right' : '', colPriority === 3 ? 'col-priority-3' : '', colPriority === 2 ? 'col-priority-2' : ''].filter(Boolean).join(' ');
							return (
								<th key={col.key} className={cls} onClick={() => handleSort(col.key)}>
									{col.label}
									{sortKey === col.key && (
										<span className="sort-arrow">
											{sortDir === 1 ? '\u25B2' : '\u25BC'}
										</span>
									)}
								</th>
							);
						})}
						{columns.some((c) => (c.priority ?? 1) <= maxVisiblePriority) && (
							<th className="actions" style={{ width: 64 }}>{' '}</th>
						)}
					</tr>
				</thead>
				<tbody>
					{sortedRows.length === 0 && loading && (
						Array.from({ length: 5 }, (_, i) => (
							<tr key={`sk-${i}`} className="skeleton-row">
								{columns
									.filter((col) => (col.priority ?? 1) <= maxVisiblePriority)
									.map((col, idx) => (
										<td key={col.key}>
											<div className={`skeleton ${idx === 0 ? 'skeleton-cell-lg' : 'skeleton-cell'}`} />
										</td>
									))}
								{columns.some((c) => (c.priority ?? 1) <= maxVisiblePriority) && (
									<td className="actions">
										<div className="skeleton skeleton-cell-sm" />
									</td>
								)}
							</tr>
						))
					)}
					{sortedRows.length === 0 && !loading && (
						<tr>
							<td colSpan={columns.filter((c) => (c.priority ?? 1) <= maxVisiblePriority).length + 1} className="empty-state">
								{error || (filterClauses.length > 0
									? <><span>{t('dataTable.noFilterMatch')}</span> <Button variant="ghost" size="sm" onClick={() => handleFilterChange([])}>{t('btn.clearFilters')}</Button></>
									: emptyMessage)}
							</td>
						</tr>
					)}
					{displayRows.map((row) => {
						if (renderRow) {
							const custom = renderRow(row, columns, maxVisiblePriority, getActionItems(row), { rowLink, navigate, returnToParam: effectiveReturnTo, dateFormat });
							if (custom) {return custom;}
						}
						const rowPath = appendReturnTo(rowLink ? rowLink(row) : undefined);
						return (
							<ClickableRow
								key={row.id ?? row.code}
								className="single-row"
								to={rowPath}
								navigate={navigate}
								onRowClick={onRowClick && !rowPath ? () => onRowClick(row) : undefined}
							>
								{columns.map((col) => {
									const colPriority = col.priority ?? 1;
									if (colPriority > maxVisiblePriority) {return null;}
									const isNumeric = col.type === 'number' || col.type === 'currency';
									const alignCls = col.align || (isNumeric ? 'right' : '');
									const wrapCls = col.getTags ? '' : (col.type === 'text' ? 'truncate' : 'nowrap');

									let display;
									if (col.getTags) {
										display = <TagPills names={col.getTags(row)} />;
									} else if (col.type === 'currency' && col.getCurrency) {
										const cur = col.getCurrency(row);
										if (cur) {
											display = <Amount masked value={cur.value} decimalPlaces={cur.decimalPlaces} symbol={cur.symbol} />;
										}
									}
									if (!display) {
										display = columnDisplay(col, row, dateFormat);
									}

									if (col.getPillVariant) {
										display = <Pill variant={col.getPillVariant(row)}>{display}</Pill>;
									}

									const tdCls = [alignCls, wrapCls].filter(Boolean).join(' ');
									return (
										<td key={col.key} className={tdCls}>
											{col.linkTo && col.linkTo(row)
												? <Link to={appendReturnTo(col.linkTo(row))} className="link">{display}</Link>
												: display}
										</td>
									);
								})}
								{columns.some((c) => (c.priority ?? 1) <= maxVisiblePriority) && (
									<td className="actions">
										<ActionMenu items={getActionItems(row)} />
									</td>
								)}
							</ClickableRow>
						);
					})}
					{hasMore && (
						<tr ref={sentinelRef} className="sentinel-row" aria-hidden>
							<td colSpan={columns.filter((c) => (c.priority ?? 1) <= maxVisiblePriority).length + 1} />
						</tr>
					)}
					{hasMorePages && (
						<tr className="load-more-row">
							<td colSpan={columns.filter((c) => (c.priority ?? 1) <= maxVisiblePriority).length + 1}>
								<div className="load-more">
									<Button
										variant="ghost"
										size="sm"
										onClick={onFetchMore}
										disabled={loadingMore}
									>
										{loadingMore ? t('dataTable.loadingMore') : t('dataTable.loadMore')}
									</Button>
								</div>
							</td>
						</tr>
					)}
				</tbody>
			</table>
		</div>
	);
}
